import React, { useState } from 'react';
import { AcaoPDCA, EtapaPDCA } from '../../models/inteligencia';
import { useMaestroContext } from '../../context/MaestroContext';
import maestroService from '../../maestroService';
import { formatarData } from '../../utils/formatters';
import { LABEL_AREA } from '../../utils/labels';
import { dispararAcaoSimulada } from '../../utils/acaoSimulada';
import Card from '../Card';
import EstadoVazio from '../EstadoVazio';

const COLUNAS: Array<{ etapa: EtapaPDCA; titulo: string; descricao: string }> = [
  { etapa: 'PLAN', titulo: 'Plan', descricao: 'Meta, riscos previstos e ações preventivas do ciclo.' },
  { etapa: 'DO', titulo: 'Do', descricao: 'Execução, entregas e evidências.' },
  { etapa: 'CHECK', titulo: 'Check', descricao: 'Planejado x realizado, causas e retrabalhos.' },
  { etapa: 'ACT', titulo: 'Action', descricao: 'Ações corretivas e preventivas transformadas em tarefas.' },
];

const COR_STATUS_PDCA: Record<AcaoPDCA['status'], string> = {
  ABERTA: 'neutro', EM_ANDAMENTO: 'info', CONCLUIDA: 'success', VENCIDA: 'danger',
};

// Situações recorrentes de fechamento — atalho para não digitar do zero; o texto continua
// editável depois de escolhido, então isso é ponto de partida, não uma opção fixa.
const PDCA_PRESETS: Array<{ titulo: string; problema: string; acaoCorretiva: string }> = [
  { titulo: 'Atraso na conciliação bancária', problema: 'Conciliação bancária não concluída dentro do prazo do ciclo.', acaoCorretiva: 'Priorizar a conciliação pendente e comunicar o responsável sobre o novo prazo.' },
  { titulo: 'Evidência não anexada', problema: 'Atividade concluída sem a evidência obrigatória anexada.', acaoCorretiva: 'Cobrar o anexo da evidência e revisar o checklist da atividade antes do próximo fechamento.' },
  { titulo: 'Divergência contábil x fiscal', problema: 'Divergência identificada entre o saldo contábil e o relatório fiscal.', acaoCorretiva: 'Investigar a origem da divergência e realizar o ajuste contábil necessário.' },
  { titulo: 'Aprovação SOX atrasada', problema: 'Aprovação SOX pendente há mais tempo do que o SLA definido.', acaoCorretiva: 'Escalar para o aprovador responsável e registrar a justificativa do atraso.' },
  { titulo: 'Dependência não cumprida', problema: 'Atividade predecessora não concluída no prazo, bloqueando a sucessora.', acaoCorretiva: 'Renegociar o prazo com a área responsável pela predecessora e ajustar o cronograma.' },
];

const FormNovaAcaoPdca: React.FC<{ onCancelar: () => void }> = ({ onCancelar }) => {
  const { cicloAtivo, empresasEscopo, recarregar } = useMaestroContext();
  const [problema, setProblema] = useState('');
  const [acaoCorretiva, setAcaoCorretiva] = useState('');
  const [empresaId, setEmpresaId] = useState<number>(empresasEscopo[0]?.id ?? 0);
  const [prazo, setPrazo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const criar = () => {
    if (!problema.trim() || !acaoCorretiva.trim() || !empresaId) return;
    setEnviando(true);
    maestroService.criarPdca({
      cicloId: cicloAtivo.id,
      empresaId,
      problema: problema.trim(),
      acaoCorretiva: acaoCorretiva.trim(),
      prazoAcao: prazo || undefined,
    })
      .then(() => { dispararAcaoSimulada('Ação de PDCA criada.'); recarregar(); onCancelar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar a ação de PDCA.'))
      .finally(() => setEnviando(false));
  };

  return (
    <div className='mc-pdca-form'>
      <div className='mc-pdca-form__presets'>
        {PDCA_PRESETS.map((preset, i) => (
          <button
            key={i}
            type='button'
            className='mc-btn mc-btn--secondary mc-btn--sm'
            onClick={() => { setProblema(preset.problema); setAcaoCorretiva(preset.acaoCorretiva); }}
          >
            {preset.titulo}
          </button>
        ))}
      </div>

      <label className='mc-campo'>Empresa
        <select value={empresaId} onChange={e => setEmpresaId(Number(e.target.value))}>
          <option value={0}>Selecione...</option>
          {empresasEscopo.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </label>
      <label className='mc-campo'>Problema
        <textarea rows={2} placeholder='O que aconteceu' value={problema} onChange={e => setProblema(e.target.value)} />
      </label>
      <label className='mc-campo'>Ação corretiva
        <textarea rows={2} placeholder='O que vai ser feito' value={acaoCorretiva} onChange={e => setAcaoCorretiva(e.target.value)} />
      </label>
      <label className='mc-campo'>Prazo
        <input type='date' value={prazo} onChange={e => setPrazo(e.target.value)} />
      </label>
      <div className='mc-pdca-form__acoes'>
        <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !problema.trim() || !acaoCorretiva.trim() || !empresaId} onClick={criar}>
          {enviando ? 'Criando...' : 'Criar ação'}
        </button>
        <button type='button' className='mc-btn mc-btn--secondary' onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
};

const CardPDCA: React.FC<{ acao: AcaoPDCA }> = ({ acao }) => {
  const { getArea, getUsuario, areas, recarregar } = useMaestroContext();
  const area = getArea(acao.areaResponsavelId);
  const responsavel = getUsuario(acao.responsavelId);
  const [escolhendoArea, setEscolhendoArea] = useState(false);
  const [areaEscolhidaId, setAreaEscolhidaId] = useState<number>(areas[0]?.id ?? 0);
  const [gerando, setGerando] = useState(false);
  const [avancando, setAvancando] = useState(false);
  const [avaliandoEficacia, setAvaliandoEficacia] = useState(false);
  const [observacaoEficacia, setObservacaoEficacia] = useState('');
  const [enviandoEficacia, setEnviandoEficacia] = useState(false);

  const gerarAtividade = () => {
    if (!areaEscolhidaId) return;
    setGerando(true);
    maestroService.gerarAtividadeDoPdca(acao.id, areaEscolhidaId)
      .then(() => { dispararAcaoSimulada('Atividade gerada a partir da ação de PDCA.'); setEscolhendoArea(false); recarregar(); })
      .catch(() => dispararAcaoSimulada('Não foi possível gerar a atividade.'))
      .finally(() => setGerando(false));
  };

  // MAESTRO-RN-37 — não existe um seletor livre de "qual etapa" na criação: a ação sempre nasce em
  // Plan e avança sozinha pelo ciclo real (Do -> Check -> Action) conforme o status muda aqui e,
  // na última etapa, a avaliação de eficácia é registrada — reflete o método PDCA de verdade, não é
  // uma categoria que se escolhe.
  const avancarStatus = (novoStatus: 'EM_ANDAMENTO' | 'CONCLUIDO') => {
    setAvancando(true);
    maestroService.atualizarStatusPdca(acao.id, acao, novoStatus)
      .then(() => { dispararAcaoSimulada(novoStatus === 'EM_ANDAMENTO' ? 'Ação movida para Do — em execução.' : 'Ação concluída — pronta para avaliar eficácia em Check.'); recarregar(); })
      .catch(() => dispararAcaoSimulada('Não foi possível atualizar o status da ação.'))
      .finally(() => setAvancando(false));
  };

  const avaliarEficacia = (eficaz: boolean) => {
    setEnviandoEficacia(true);
    maestroService.registrarEficaciaPdca(acao.id, eficaz, observacaoEficacia.trim() || undefined)
      .then(() => { dispararAcaoSimulada('Avaliação de eficácia registrada — ação movida para Action.'); setAvaliandoEficacia(false); setObservacaoEficacia(''); recarregar(); })
      .catch(() => dispararAcaoSimulada('Não foi possível registrar a avaliação de eficácia.'))
      .finally(() => setEnviandoEficacia(false));
  };

  return (
    <div className='mc-pdca-card'>
      <div className='mc-pdca-card__topo'>
        <span className={`mc-badge mc-badge--${COR_STATUS_PDCA[acao.status]}`}>{acao.status.replace('_', ' ')}</span>
      </div>
      <strong>{acao.problema}</strong>
      {acao.causa && <p><strong>Causa:</strong> {acao.causa}</p>}
      {acao.acaoCorretiva && <p><strong>Ação corretiva:</strong> {acao.acaoCorretiva}</p>}
      {acao.acaoPreventiva && <p><strong>Ação preventiva:</strong> {acao.acaoPreventiva}</p>}
      {acao.metaEsperada && <p><strong>Meta:</strong> {acao.metaEsperada}</p>}
      {acao.resultado && <p className='mc-texto-success'><strong>Resultado:</strong> {acao.resultado}</p>}
      <div className='mc-pdca-card__rodape'>
        <span>{area ? LABEL_AREA[area.tipo] : ''} · {responsavel?.nome}</span>
        <span>Prazo: {formatarData(acao.prazo)}</span>
      </div>

      <div className='mc-pdca-card__acoes'>
        {acao.etapa === 'PLAN' && (
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={avancando} onClick={() => avancarStatus('EM_ANDAMENTO')}>
            {avancando ? 'Movendo...' : 'Iniciar execução (→ Do)'}
          </button>
        )}
        {acao.etapa === 'DO' && (
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={avancando} onClick={() => avancarStatus('CONCLUIDO')}>
            {avancando ? 'Movendo...' : 'Concluir ação (→ Check)'}
          </button>
        )}
        {acao.acaoCorretiva && !escolhendoArea && (
          <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setEscolhendoArea(true)}>
            Gerar atividade
          </button>
        )}
      </div>

      {escolhendoArea && (
        <div className='mc-pdca-card__gerar-atividade'>
          <select value={areaEscolhidaId} onChange={e => setAreaEscolhidaId(Number(e.target.value))}>
            {areas.map(a => <option key={a.id} value={a.id}>{LABEL_AREA[a.tipo]}</option>)}
          </select>
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={gerando} onClick={gerarAtividade}>Confirmar</button>
          <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setEscolhendoArea(false)}>Cancelar</button>
        </div>
      )}

      {acao.etapa === 'CHECK' && !avaliandoEficacia && (
        <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setAvaliandoEficacia(true)}>
          Avaliar eficácia (→ Action)
        </button>
      )}
      {acao.etapa === 'CHECK' && avaliandoEficacia && (
        <div className='mc-pdca-card__gerar-atividade'>
          <textarea rows={2} placeholder='Observação da avaliação (opcional)' value={observacaoEficacia} onChange={e => setObservacaoEficacia(e.target.value)} />
          <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={enviandoEficacia} onClick={() => avaliarEficacia(true)}>Foi eficaz</button>
          <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' disabled={enviandoEficacia} onClick={() => avaliarEficacia(false)}>Não foi eficaz</button>
        </div>
      )}
    </div>
  );
};

const VisaoPDCA: React.FC = () => {
  const { acoesPDCA, cicloAtivo } = useMaestroContext();
  const [criando, setCriando] = useState(false);

  const cabecalho = cicloAtivo.id && (
    <div className='mc-pdca-board__cabecalho'>
      {!criando ? (
        <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setCriando(true)}>
          <i className='bi bi-plus-lg' /> Nova ação de PDCA
        </button>
      ) : (
        <FormNovaAcaoPdca onCancelar={() => setCriando(false)} />
      )}
    </div>
  );

  if (acoesPDCA.length === 0) {
    return (
      <div className='mc-pdca-board mc-pdca-board--vazio'>
        {cabecalho}
        <EstadoVazio icone='bi bi-clipboard-pulse' titulo='Nenhuma ação de PDCA neste ciclo' descricao='As recomendações da Inteligência do Maestro podem gerar novas ações de PDCA, ou você pode criar uma manualmente acima.' />
      </div>
    );
  }

  return (
    <div className='mc-pdca-board'>
      {cabecalho}
      <div className='mc-pdca-board__colunas'>
        {COLUNAS.map(coluna => {
          const acoes = acoesPDCA.filter(a => a.etapa === coluna.etapa);
          return (
            <Card key={coluna.etapa} title={coluna.titulo} subtitle={coluna.descricao} className='mc-pdca-coluna'>
              {acoes.length === 0
                ? <EstadoVazio titulo='Nenhuma ação nesta etapa' />
                : acoes.map(acao => <CardPDCA key={acao.id} acao={acao} />)}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default VisaoPDCA;
